import { createContext, useContext, useState, useEffect, useRef, type ReactNode } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/context/AuthContext'
import type { Language, Translations } from './types'
import { en } from './en'
import { ro } from './ro'
import { setCrmLocale, setCrmLanguage } from './crm/locale'

const translations: Record<Language, Translations> = { en, ro }

interface LanguageContextType {
  language: Language
  setLanguage: (lang: Language) => void
  t: Translations
}

const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: en,
})

const STORAGE_KEY = 'evtimia-lang'

function readStored(): Language | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'ro' || stored === 'en') return stored
  } catch {
    // Storage blocked: no remembered choice.
  }
  return null
}

/** A browser that lists Romanian among its languages gets Romanian; anyone else English. */
function browserLanguage(): Language {
  const langs = typeof navigator === 'undefined' ? [] : (navigator.languages ?? [navigator.language])
  return langs.some(l => l?.toLowerCase().startsWith('ro')) ? 'ro' : 'en'
}

/**
 * The language lives in localStorage, not the URL: there are no /ro/ routes.
 *
 * Until someone picks one with the switch, it follows the browser (from
 * 30 October 2026; before that everyone started in English). Signed in, it is
 * also the account's language, `notification_preferences.locale`, which emails
 * and push messages are written in: the switch saves it there, and a device
 * with no choice of its own takes the account's.
 */
export function LanguageProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const userId = user?.id
  const [language, setLanguageState] = useState<Language>(() => readStored() ?? browserLanguage())

  const setLanguage = (lang: Language) => {
    setLanguageState(lang)
    // Mirrors the guarded read above. Unguarded, the switch applies and then
    // throws, so the choice silently fails to persist and reverts on reload.
    try {
      localStorage.setItem(STORAGE_KEY, lang)
    } catch {
      // Storage blocked (private browsing, blocked site data). The language
      // still applies for this session; it just will not be remembered.
    }
  }

  // On sign-in: this device's own choice wins and is saved to the account;
  // with none, the account's language is adopted. `saved` mirrors what the
  // account holds, so the effect below writes only real changes.
  const saved = useRef<Language | null>(null)
  const [accountRead, setAccountRead] = useState(false)
  useEffect(() => {
    saved.current = null
    setAccountRead(false)
    if (!userId) return
    let cancelled = false
    void (async () => {
      const { data } = await supabase
        .from('notification_preferences')
        .select('locale')
        .eq('user_id', userId)
        .maybeSingle()
      if (cancelled) return
      const account: Language | null = data?.locale === 'ro' || data?.locale === 'en' ? data.locale : null
      saved.current = account
      if (account && !readStored()) setLanguage(account)
      setAccountRead(true)
    })()
    return () => {
      cancelled = true
    }
  }, [userId])

  // Once the account is read, save any language that differs from it: the
  // device's choice at sign-in, then every use of the switch. Best effort: a
  // failed save leaves emails in the old language until the next switch or
  // sign-in, which is not worth an error message.
  useEffect(() => {
    if (!userId || !accountRead || saved.current === language) return
    saved.current = language
    // A supabase query is sent only when it is awaited or .then() is called;
    // `void` alone left this save unsent (found 30 October 2026). A failed
    // save forgets the language, so the next switch or sign-in tries again.
    supabase
      .from('notification_preferences')
      .upsert({ user_id: userId, locale: language }, { onConflict: 'user_id' })
      .then(({ error }) => {
        if (error && saved.current === language) saved.current = null
      })
  }, [userId, language, accountRead])

  // Deliberately synchronous: date helpers read this during the same render, so
  // deferring it to an effect would paint the first frame in the wrong locale.
  setCrmLocale(language)
  setCrmLanguage(language)

  useEffect(() => {
    document.documentElement.lang = language
  }, [language])

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t: translations[language] }}>
      {children}
    </LanguageContext.Provider>
  )
}

export function useTranslation() {
  return useContext(LanguageContext)
}
